import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { getMe, loginUser, logoutUser, verifyMfaCode } from "../services/userService";

const INACTIVITY_LIMIT = 30 * 60 * 1000;
const ACTIVITY_EVENTS  = ["mousemove", "keydown", "click", "scroll", "touchstart"];

const AuthContext = createContext({
  user:      null,
  loading:   true,
  login:     async () => {},
  logout:    async () => {},
  verifyMfa: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {

  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  const logout = useCallback(async () => {
    clearTimeout(inactivityTimer.current);
    await logoutUser();
    setUser(null);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => logout(), INACTIVITY_LIMIT);
  }, [logout]);

  const startInactivityWatch = useCallback(() => {
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, resetInactivityTimer));
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const stopInactivityWatch = useCallback(() => {
    ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, resetInactivityTimer));
    clearTimeout(inactivityTimer.current);
  }, [resetInactivityTimer]);

  useEffect(() => {
    const handleExpired = () => {
      clearTimeout(inactivityTimer.current);
      stopInactivityWatch();
      setUser(null);
    };
    window.addEventListener("auth:session-expired", handleExpired);
    return () => window.removeEventListener("auth:session-expired", handleExpired);
  }, [stopInactivityWatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }

    getMe()
      .then(res => { setUser(res.data); startInactivityWatch(); })
      .catch(() => { localStorage.removeItem("token"); setUser(null); })
      .finally(() => setLoading(false));

    return () => stopInactivityWatch();
  }, []);

  // ÉTAPE 1 — Vérifier email + mot de passe
  // Si MFA requis → retourne { requiresMfa: true, maskedEmail }
  // LoginPage redirige vers /verify-mfa, on ne charge pas encore le profil
  const login = async (credentials) => {
    const result = await loginUser(credentials);
    if (result.requiresMfa) return result;

    // Connexion directe sans MFA
    const profileRes = await getMe();
    setUser(profileRes.data);
    startInactivityWatch();
    return result;
  };

  // ÉTAPE 2 — Vérifier le code MFA
  // Si le code est correct → charge le profil et démarre la session
  const verifyMfa = async (code, rememberMe = false) => {
    await verifyMfaCode(code, rememberMe);
    const profileRes = await getMe();
    setUser(profileRes.data);
    startInactivityWatch();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verifyMfa }}>
      {children}
    </AuthContext.Provider>
  );
}